import React, { useEffect, useState, useCallback, useRef } from 'react';
import useResizeNotifier from '../../hooks/useResizeNotifier';
import { observer } from 'mobx-react';
import moment from 'moment-timezone';
import Heatmap from '../../Components/Heatmap/Heatmap';
import './SectorHeatmapWidget.css';
import Tooltip from '../../Components/Heatmap/Tooltip';
import CommonTooltip from '../../Components/Common/CommonTooltip/CommonTooltip';
import { BaseWidgetProps, WidgetHeightMessage } from '../../types/common.type';
//css for tooltip
import '../../Components/Heatmap/Tooltip.css';

import { ReactComponent as ArrowLeft } from '../../assets/icons/arrow-left-s-line.svg';
import { getSectorHeatmapData } from '../../api/heatmap.api';
import { SectorData } from '../../types/indices.type';
import { formatKMBT, formatNumber } from '../../utils/utils';
import {
  Region,
  TimePeriod,
  ThemeType,
  WidgetMessageType,
  WidgetNameSource,
} from '../../utils/type';

import Loading from '../../Components/Loading/Loading';
import { ReactComponent as ErrorIcon } from '../../assets/icons/error-state.svg';
import ErrorMessage from '../../Components/ErrorMessage/ErrorMessage';
import { errorMsg } from '../../constants/common.constant';
import Layout from '../../Components/Common/Layout/Layout';

export interface SectorHeatmapWidgetProps extends BaseWidgetProps {
  region?: Region; //AU, US.
  period?: TimePeriod; //1D, 1M, 3M, 6M, 1YR , default is '1D'
  token?: string;
  height?: number; //pixel height of the heatmap
  onCallback?: (message: SectorData) => void;
  onHeightChange?: (message: WidgetHeightMessage) => void;
}
const SectorHeatmapWidget = (props: SectorHeatmapWidgetProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  useResizeNotifier(
    containerRef,
    WidgetNameSource.SectorHeatmap,
    props.onHeightChange,
  );
  const [isLoading, setIsLoading] = useState(true);
  const onLoadingChangeRef = useRef(props.onLoadingChange);
  onLoadingChangeRef.current = props.onLoadingChange;
  useEffect(() => {
    onLoadingChangeRef.current?.(isLoading);
  }, [isLoading]);
  const [isEmptyData, setIsEmptyData] = useState(false);
  const msgItem = errorMsg.find((obj) => obj.key === 'heatmap');

  const [showBackButton, setShowBackButton] = useState<boolean>(false);
  const [showSubitem, setShowSubitem] = useState<boolean>(false);
  const [heatmapDataRaw, setHeatmapDataRaw] = useState<any[]>([]);
  const [heatmapData, setHeatmapData] = useState<any[]>([]);
  const [prevHeatmapData, setPrevHeatmapData] = useState<any[]>([]);
  const [heatmapDataLv1, setHeatmapDataLv1] = useState<any[]>([]);
  const [colorby, setColorby] = useState<string>('pc');
  const [updateTime, setUpdateTime] = useState<number>(0);
  const [tooltip, setTooltip] = useState<any>({
    content: '',
    visible: false,
    x: 0,
    y: 0,
    ric: '',
  });
  // Debounce timer ref
  const tooltipTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Debounced setTooltip function
  const debouncedSetTooltip = useCallback(
    (newTooltip: any, delay: number = 5) => {
      // Clear existing timeout
      if (tooltipTimeoutRef.current) {
        clearTimeout(tooltipTimeoutRef.current);
      }

      // Set new timeout
      tooltipTimeoutRef.current = setTimeout(() => {
        setTooltip(newTooltip);
      }, delay);
    },
    [],
  );

  useEffect(() => {
    window.addEventListener('resize', () => {
      setUpdateTime(Date.now());
    });
    return () => {
      window.removeEventListener('resize', () => {
        setUpdateTime(Date.now());
      });
    };
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      setUpdateTime(Date.now());
    }, 2000);
    return () => {
      clearInterval(interval);
    };
  }, []);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (tooltipTimeoutRef.current) {
        clearTimeout(tooltipTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    setShowBackButton(false);
    setHeatmapData([]);
    setHeatmapDataLv1([]);
    setPrevHeatmapData([]);
    setUpdateTime(Date.now());
    setIsEmptyData(false);
    setIsLoading(true);
    getSectorHeatmapData(props.region || undefined, props.token || '')
      .then((data) => {
        if (data.length === 0) {
          setIsEmptyData(true);
          props.onNoData?.({
            type: WidgetMessageType.NoData,
            source: 'SectorHeatmapWidget',
            data: null,
          });
        }
        setHeatmapDataRaw(data);
      })
      .catch((err) => {
        props.onError?.({
          message: err.message,
          type: err.name + '_' + err.code,
        });
        setIsEmptyData(true);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [props.region]);

  useEffect(() => {
    if (heatmapDataRaw.length > 0) {
      chgHeatMapData(heatmapDataRaw);
    }
  }, [heatmapDataRaw]);

  // Initial state for reset functionality
  const initialHeatmapColor = [
    {
      color: '#085026',
      fontColor: '#ffffff',
      min: 5,
      max: 9999.0,
      label: '+5%',
    },
    { color: '#11773c', fontColor: '#ffffff', min: 4, max: 5, label: '+4%' },
    { color: '#188647', fontColor: '#ffffff', min: 3, max: 4, label: '+3%' },
    { color: '#1C7D45', fontColor: '#ffffff', min: 2, max: 3, label: '+2%' },
    { color: '#34b66b', fontColor: '#ffffff', min: 1, max: 2, label: '+1%' },
    {
      color: '#99ddb6',
      fontColor: '#ffffff',
      min: 0.5,
      max: 1,
      label: '+0.5%',
    },
    {
      color: '#dfdfdf',
      fontColor: '#000000',
      min: -0.5,
      max: 0.5,
      label: '0%',
    },
    {
      color: '#ffbcbc',
      fontColor: '#ffffff',
      min: -1,
      max: -0.5,
      label: '-0.5%',
    },
    {
      color: '#f18989',
      fontColor: '#ffffff',
      min: -2.0,
      max: -1.0,
      label: '-1%',
    },
    {
      color: '#e73838',
      fontColor: '#ffffff',
      min: -3.0,
      max: -2.0,
      label: '-2%',
    },
    {
      color: '#d81f1f',
      fontColor: '#ffffff',
      min: -4.0,
      max: -3.0,
      label: '-3%',
    },
    {
      color: '#b21b1b',
      fontColor: '#ffffff',
      min: -5.0,
      max: -4.0,
      label: '-4%',
    },
    {
      color: '#9e1111',
      fontColor: '#ffffff',
      min: -9999.0,
      max: -5.0,
      label: '-5%',
    },
  ];

  const initialHeatmapColor2 = [
    {
      color: '#090C1D',
      fontColor: '#ffffff',
      min: 200,
      max: 9999999.0,
      label: '200.00+',
    },
    {
      color: '#111839',
      fontColor: '#ffffff',
      min: 100,
      max: 200,
      label: '100.00',
    },
    {
      color: '#1A2456',
      fontColor: '#ffffff',
      min: 50,
      max: 100,
      label: '50.00',
    },
    {
      color: '#223072',
      fontColor: '#ffffff',
      min: 30,
      max: 50,
      label: '30.00',
    },
    {
      color: '#2B3C8F',
      fontColor: '#ffffff',
      min: 20,
      max: 30,
      label: '20.00',
    },
    {
      color: '#5563A5',
      fontColor: '#ffffff',
      min: 10,
      max: 20,
      label: '10.00',
    },
    { color: '#808ABC', fontColor: '#ffffff', min: 5, max: 10, label: '5.00' },
    { color: '#AAB1D2', fontColor: '#090C1D', min: 2, max: 5, label: '2.00' },
    {
      color: '#D5D8E9',
      fontColor: '#090C1D',
      min: -9999,
      max: 2,
      label: '1.00',
    },
  ];

  // Initial state for reset functionality
  const initialHeatmapColor3 = [
    {
      color: '#085026',
      fontColor: '#ffffff',
      min: 5,
      max: 9999.0,
      label: '+5',
    },
    { color: '#11773c', fontColor: '#ffffff', min: 4, max: 5, label: '+4' },
    { color: '#188647', fontColor: '#ffffff', min: 3, max: 4, label: '+3' },
    { color: '#1C7D45', fontColor: '#ffffff', min: 2, max: 3, label: '+2' },
    { color: '#34b66b', fontColor: '#ffffff', min: 1, max: 2, label: '+1' },
    { color: '#99ddb6', fontColor: '#ffffff', min: 0.5, max: 1, label: '+0.5' },
    { color: '#dfdfdf', fontColor: '#000000', min: -0.5, max: 0.5, label: '0' },
    {
      color: '#ffbcbc',
      fontColor: '#ffffff',
      min: -1,
      max: -0.5,
      label: '-0.5',
    },
    {
      color: '#f18989',
      fontColor: '#ffffff',
      min: -2.0,
      max: -1.0,
      label: '-1',
    },
    {
      color: '#e73838',
      fontColor: '#ffffff',
      min: -3.0,
      max: -2.0,
      label: '-2',
    },
    {
      color: '#d81f1f',
      fontColor: '#ffffff',
      min: -4.0,
      max: -3.0,
      label: '-3',
    },
    {
      color: '#b21b1b',
      fontColor: '#ffffff',
      min: -5.0,
      max: -4.0,
      label: '-4',
    },
    {
      color: '#9e1111',
      fontColor: '#ffffff',
      min: -9999.0,
      max: -5.0,
      label: '-5',
    },
  ];

  const [heatmapColor, setHeatmapColor] = useState<
    {
      color: string;
      fontColor: string;
      min: number;
      max: number;
      label: string;
    }[]
  >(initialHeatmapColor);

  useEffect(() => {
    if (heatmapData.length > 0) {
      //swap heatmapData perf1D to pc.
      let newHeatmapData = heatmapData.map((item) => {
        if (props.period === '1D') {
          item.pc = item.perf1D;
        } else if (props.period === '1M') {
          item.pc = item.perf1M;
        } else if (props.period === '3M') {
          item.pc = item.perf3M;
        } else if (props.period === '6M') {
          item.pc = item.perf6M;
        } else if (props.period === '1YR') {
          item.pc = item.perf1Y;
        } else {
          item.pc = item.perf1D;
        }
        return item;
      });
      setHeatmapData(newHeatmapData);
    }
  }, [props.period]);

  // Function to swap between value and value2
  const swapValueFields = (data: any[]) => {
    // Update the grouped data with the new value field
    let updatedGroupedData = data.map((category) => {
      //console.log('category', category);
      const newCategory = { ...category };
      //newCategory.value = category[props.sizeby || 'value'];
      if (props.period === '1D') {
        newCategory.pc = newCategory.perf1D;
      } else if (props.period === '1M') {
        newCategory.pc = newCategory.perf1M;
      } else if (props.period === '3M') {
        newCategory.pc = newCategory.perf3M;
      } else if (props.period === '6M') {
        newCategory.pc = newCategory.perf6M;
      } else if (props.period === '1YR') {
        newCategory.pc = newCategory.perf1Y;
      } else {
        newCategory.pc = newCategory.perf1D;
      }
      newCategory.label = undefined;

      return newCategory;
    });
    //updatedGroupedData.sort((a: any, b: any) => b.value - a.value).map(category => {
    //    console.log(category.name, 'props.sizeby',category[props.sizeby || 'value']);

    //});

    // Sort by the new value field
    const sortedData = updatedGroupedData.sort(
      (a: any, b: any) => b.value - a.value,
    );

    //console.log('sortedData', sortedData);
    return sortedData;
  };

  // Function to group data by categoryid
  //const groupDataByCategory = (data: any[]) => {
  //    //return []
  //    const grouped = data.reduce((acc, item) => {
  //        const categoryId = item.categoryid;

  //        if (!acc[categoryId]) {
  //            acc[categoryId] = {
  //                name: item.category,
  //                //label: item.category,
  //                categoryid: categoryId,
  //                category: item.category,
  //                subcategoryid: item.subcategoryid,
  //                subcategory: item.subcategory,
  //                ls: 0,
  //                nc: 0,
  //                pc: 0,
  //                value: 0,
  //                value1: 0,
  //                value2: 0,
  //                subitem: []
  //            };
  //        }
  //        acc[categoryId].subitem.push(item);
  //        acc[categoryId].nc += item.nc || 0;
  //        acc[categoryId].pc += item.pc || 0;
  //        acc[categoryId].ls += item.ls || 0;
  //        acc[categoryId].volume += item.volume || 0;
  //        acc[categoryId].turnover += item.turnover || 0;
  //        acc[categoryId].mktcap += item.mktcap || 0;

  //        acc[categoryId].value += item[props.sizeby || 'value'] || 0;

  //        return acc;
  //    }, {});

  //    // Convert to array and sort by total value
  //    return Object.values(grouped).sort((a: any, b: any) => b.value - a.value);
  //};
  //// Function to group data by categoryid
  //const groupDataBySubCategory = (data: any[]) => {
  //    const grouped = data.reduce((acc, item) => {
  //        const subcategoryId = item.subcategoryid;
  //        if (!acc[subcategoryId]) {
  //            acc[subcategoryId] = {
  //                name: item.subcategory,
  //                //label: item.category,
  //                categoryid:  item.categoryid,
  //                category: item.category,
  //                subcategoryid: subcategoryId,
  //                subcategory: item.subcategory,
  //                ls: 0,
  //                nc: 0,
  //                pc: 0,
  //                value: 0,
  //                value1: 0,
  //                value2: 0,
  //                subitem: []
  //            };
  //        }
  //        acc[subcategoryId].subitem.push(item);
  //        acc[subcategoryId].value += item.value || 0;
  //        acc[subcategoryId].value1 += item.value1 || 0;
  //        acc[subcategoryId].value2 += item.value2 || 0;
  //        return acc;
  //    }, {});

  //    // Calculate aggregated values for each category
  //    Object.values(grouped).forEach((subcategory: any) => {
  //        if (subcategory.subitem.length > 0) {
  //            // Calculate average percentage change
  //            const totalPc = subcategory.subitem.reduce((sum: number, item: any) => sum + (item.pc || 0), 0);
  //            subcategory.pc = (totalPc / subcategory.subitem.length);

  //            // Calculate average last price
  //            const totalLs = subcategory.subitem.reduce((sum: number, item: any) => sum + (item.ls || 0), 0);
  //            subcategory.ls = (totalLs / subcategory.subitem.length);

  //            // Calculate total net change
  //            const totalNc = subcategory.subitem.reduce((sum: number, item: any) => sum + (item.nc || 0), 0);
  //            subcategory.nc = totalNc;
  //        }
  //    });

  //    // Convert to array and sort by total value
  //    return Object.values(grouped).sort((a: any, b: any) => b.value - a.value);
  //};
  const handleHeatmapColorChange = (color: string, index: number) => {
    setTimeout(() => {
      const newColors = [...heatmapColor];
      newColors[index].color = '#' + color;
      setHeatmapColor(newColors);
    }, 100);
  };

  const handleReset = () => {
    setHeatmapColor([...initialHeatmapColor]);
    setShowSubitem(false);
  };

  const resetHeatmapData = () => {
    setHeatmapData(swapValueFields(heatmapDataRaw));
  };

  const chgHeatMapData = async (data: any[]) => {
    //clear tooltip
    setTooltip({
      content: '',
      visible: false,
      x: 0,
      y: 0,
      ric: '',
    });
    //setHeatmapData(data.sort((a, b) =>b.value - a.value).slice(0, MaxCells));
    if (data.length > 0) {
      if (data[0].name === data[0].subcategory) setShowSubitem(true);
      else setShowSubitem(false);
    } else {
      setShowSubitem(false);
    }
    let groupedData = heatmapDataRaw;

    setPrevHeatmapData(swapValueFields(groupedData));
    setHeatmapData(swapValueFields(data));
    setShowSubitem(false);
    setShowBackButton(false);
  };

  // Default tooltip template
  const defaultTemplate = `
        <div class="sectorheatmap-tooltip-performance">
            <div class="sectorheatmap-tooltip-ticker">{{ticker}}</div>
            <div class="sectorheatmap-tooltip-metric"><div class="sectorheatmap-tooltip-metric-label">Mkt Cap{{currency}} </div> <div class="sectorheatmap-tooltip-metric-label-right">{{marketCap}}</div></div>
            <div class="sectorheatmap-tooltip-separator"></div>
            <div class="sectorheatmap-tooltip-metric"><div class="sectorheatmap-tooltip-metric-label">1 Day </div> <div class="sectorheatmap-tooltip-metric-label-right">{{perf1D}}</div></div>
            <div class="sectorheatmap-tooltip-metric"><div class="sectorheatmap-tooltip-metric-label">1 Month </div> <div class="sectorheatmap-tooltip-metric-label-right">{{perf1M}}</div></div>
            <div class="sectorheatmap-tooltip-metric"><div class="sectorheatmap-tooltip-metric-label">3 Months </div> <div class="sectorheatmap-tooltip-metric-label-right">{{perf3M}}</div></div>
            <div class="sectorheatmap-tooltip-metric"><div class="sectorheatmap-tooltip-metric-label">6 Months </div> <div class="sectorheatmap-tooltip-metric-label-right">{{perf6M}}</div></div>
            <div class="sectorheatmap-tooltip-metric"><div class="sectorheatmap-tooltip-metric-label">1 Year </div> <div class="sectorheatmap-tooltip-metric-label-right">{{perf1Y}}</div></div>
        </div>`;

  // Use provided template or default
  const template = defaultTemplate;
  const tooltipCallback = (newTooltip: any) => {
    //check if tooltip.x,tooltip.y is same as last tooltip.x,tooltip.y, if same, return
    if (tooltip.show == true && newTooltip.show == false) {
      // For hiding tooltip, update immediately without debounce
      debouncedSetTooltip({ ...newTooltip, show: false });
      return;
    }
    if (newTooltip.ric === '') {
      return;
    }
    let result = template;
    //find item by tooltip.ric, from heatmapDataRaw
    const item = heatmapDataRaw.find(
      (item: any) => item.name === newTooltip.ric,
    );
    if (!item) {
      return;
    }
    // Prepare data values
    let marketCap = item.mktcap
      ? formatKMBT(item.mktcap, 3, 3, false)
      : { value: '-', suffix: '' };
    const data = {
      ticker: item.name || '-',
      perf1D: item.perf1D
        ? `<span class="${getUpDownClass(item.perf1D)}">${formatNumber(item.perf1D, 2, 2, false, true, false, '%')}</span>`
        : '-',
      perf1M: item.perf1M
        ? `<span class="${getUpDownClass(item.perf1M)}">${formatNumber(item.perf1M, 2, 2, false, true, false, '%')}</span>`
        : '-',
      perf3M: item.perf3M
        ? `<span class="${getUpDownClass(item.perf3M)}">${formatNumber(item.perf3M, 2, 2, false, true, false, '%')}</span>`
        : '-',
      perf6M: item.perf6M
        ? `<span class="${getUpDownClass(item.perf6M)}">${formatNumber(item.perf6M, 2, 2, false, true, false, '%')}</span>`
        : '-',
      perf1Y: item.perf1Y
        ? `<span class="${getUpDownClass(item.perf1Y)}">${formatNumber(item.perf1Y, 2, 2, false, true, false, '%')}</span>`
        : '-',
      marketCap: item.mktcap ? `${marketCap.value}${marketCap.suffix}` : '-',
      currency: item.currency ? ` (${item.currency})` : '',
    };

    // Replace template variables
    let c = template;
    Object.entries(data).forEach(([key, value]) => {
      const regex = new RegExp(`{{${key}}}`, 'g');
      result = result.replace(regex, String(value));
    });
    // Clean up empty lines and extra spacing
    result = result.replace(/\n\s*\n\s*\n/g, '\n\n');

    // Calculate position relative to body
    const elementMaxWidth = 200;
    const elementMaxHeight = 240;
    const scrollX = window.pageXOffset || document.documentElement.scrollLeft;
    const scrollY = window.pageYOffset || document.documentElement.scrollTop;
    const viewportWidth = window.innerWidth + scrollX;
    const viewportHeight = window.innerHeight + scrollY;

    if (newTooltip.x + elementMaxWidth > viewportWidth) {
      newTooltip.x = newTooltip.x - elementMaxWidth;
    }
    if (newTooltip.y + elementMaxHeight > viewportHeight) {
      newTooltip.y = newTooltip.y - elementMaxHeight;
    }

    //check if newTooltip.x less than 0, set it to 0
    if (newTooltip.x < 0) {
      newTooltip.x = 5;
    }
    //check if newTooltip.y less than 0, set it to 0
    if (newTooltip.y < 0) {
      newTooltip.y = 5;
    }

    // Use debounced setTooltip for showing tooltip
    debouncedSetTooltip({
      ...newTooltip,
      content: result,
    });
  };

  const onClickCallback = (item: any) => {
    console.log('onClickCallback', item);
    if (props.onCallback && item.category) {
      let message = {
        data: { sector: item.category },
        source: 'sectorheatmap',
        type: WidgetMessageType.Sector,
      };
      if ((window as any).ReactNativeWebView) {
        (window as any).ReactNativeWebView.postMessage(
          JSON.stringify(message),
          '*',
        );
      }

      window.parent.postMessage(JSON.stringify(message), '*');
      props.onCallback(message);
    }
  };
  function getUpDownClass(value: number): string {
    let updownclass = '';
    if (value) {
      if (value > 0) updownclass = 'positive';
      else if (value < 0) updownclass = 'negative';
      else if ((value = 0)) updownclass = '';
    }

    return updownclass;
  }
  return (
    <Layout theme={props?.theme || ThemeType.Direct}>
      <div
        className="sectorheatmap-container"
        ref={containerRef}
        style={{ height: props.height ? props.height + 'px' : '500px' }}
      >
        {showBackButton && (
          <div className="heatmapboxtitletext">
            <div>{heatmapData.length > 0 ? heatmapData[0].category : ''} </div>
            <div
              style={{ cursor: 'pointer' }}
              onClick={() => {
                resetHeatmapData();
              }}
            >
              <ArrowLeft /> Back to all Sectors
            </div>
          </div>
        )}
        {isLoading && !props.onLoadingChange ? (
          <Loading />
        ) : !isLoading && isEmptyData ? (
          <ErrorMessage msg={msgItem} />
        ) : (
          <div className="sectorheatmap-chart-wrapper">
            {heatmapData.length > 0 && (
              <Heatmap
                page="mini"
                updateTime={updateTime}
                data={heatmapData}
                isGrouped={true}
                heatmapColor={heatmapColor}
                colorBy={colorby}
                showSubitem={showSubitem}
                chgHeatMapData={chgHeatMapData}
                resetHeatmapData={resetHeatmapData}
                tooltipCallback={tooltipCallback}
                onClickCallback={onClickCallback}
                showLabel={true}
              />
            )}
            {heatmapData.length > 0 && (
              <div className="sectorheatmap-color-range-footer-container">
                {/*<div className="sectorheatmap-color-range-footer" style={{ background: 'linear-gradient(to right, ' + heatmapColor.reverse().map((item) => item.color).join(', ') + ')' }}>*/}
                <div className="sectorheatmap-color-range-footer">
                  {heatmapColor.map((item, index) => (
                    <div
                      key={'sectorheatmap-color-range-footer' + index}
                      className="sectorheatmap-color-range-footer-item-container"
                    >
                      <div
                        className="sectorheatmap-color-range-footer-item-color"
                        style={{
                          backgroundColor: item.color,
                          color: item.fontColor,
                        }}
                      ></div>
                      <div className="sectorheatmap-color-range-footer-item-text">
                        {item.label}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
        {/* horizontal color range footer */}
        {heatmapData.length > 0 && (
          <div className="sectorheatmap-color-range-horizontal-footer-container">
            {/*<div className="sectorheatmap-color-range-horizontal-footer" style={{ background: 'linear-gradient(to right, ' + heatmapColor.reverse().map((item) => item.color).join(', ') + ')' }}>*/}
            <div className="sectorheatmap-color-range-horizontal-footer">
              {heatmapColor.map((item, index) => (
                <div
                  key={'sectorheatmap-color-range-horizontal-footer' + index}
                  className="sectorheatmap-color-range-horizontal-footer-item-container"
                >
                  <div
                    className="sectorheatmap-color-range-horizontal-footer-item-color"
                    style={{
                      backgroundColor: item.color,
                      color: item.fontColor,
                    }}
                  ></div>
                  <div className="sectorheatmap-color-range-horizontal-footer-item-text">
                    {item.label}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
        <CommonTooltip
          content={tooltip.content}
          show={tooltip.show}
          x={tooltip.x}
          y={tooltip.y}
        />
        {/*<Tooltip
                content={tooltip.content}
                visible={tooltip.show}
                x={tooltip.x}
                y={tooltip.y}
                />*/}
      </div>
    </Layout>
  );
};

export default observer(SectorHeatmapWidget);
